import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  Fragment,
} from 'react';

import { HR } from './hr';
import { OpenedReserveWidget } from './openedReserveWidget';
import { ReserveWidget } from './reserveWidget';

export interface ReservesListContainerRef {
  scrollToReserve: (reserveId: number) => void;
}

interface ReservesListContainerProps {
  reservesView: Array<GM.ReservesSchemas.ScheduleReserve>;
  tablesSchedule: GM.ReservesSchemas.TablesSchedule;
  restaurantTz?: string;
  selectedReserveId?: number;
  isTableView?: boolean;
  onReserveClick?: (reserveId: number) => void;
  onReserveExpand?: (reserveId: number) => void;
  onReserveCollapse?: (reserveId: number) => void;
  onCancel?: (reserve: GM.ReservesSchemas.ScheduleReserve) => void;
  isCancelDisabled?: boolean;
  isLoading?: boolean;
  renderEmpty?: () => React.ReactNode;
  compact?: boolean;
  children?: (props: {
    reservesView: Array<GM.ReservesSchemas.ScheduleReserve>;
    openedReserveIds: number[];
    forceClosedReserveIds: number[];
    toggleOpenedReserveId: (id: number) => void;
    isReserveOpened: (id: number) => boolean;
  }) => React.ReactNode;
}

export const ReservesListContainer = forwardRef<
  ReservesListContainerRef,
  ReservesListContainerProps
>(
  (
    {
      reservesView,
      tablesSchedule,
      restaurantTz,
      selectedReserveId,
      isTableView = false,
      onReserveClick,
      onReserveExpand,
      onReserveCollapse,
      onCancel,
      isCancelDisabled = false,
      isLoading = false,
      renderEmpty,
      compact = false,
      children,
    },
    ref,
  ) => {
    const [openedReserveIds, setOpenedReserveIds] = useState<number[]>([]);
    const [forceClosedReserveIds, setForceClosedReserveIds] = useState<number[]>([]);
    const scrollToRefs = React.useRef<Map<number, HTMLElement>>(new Map());

    const toggleOpenedReserveId = (id: number) => {
      const isOpenedNow = openedReserveIds.includes(id);

      setForceClosedReserveIds((prev) => {
        if (!isOpenedNow) {
          return prev.filter((rid) => rid !== id);
        }
        return [...prev, id];
      });

      setOpenedReserveIds((prev) => {
        if (isOpenedNow) {
          return prev.filter((rid) => rid !== id);
        }
        return [id];
      });
    };

    const isReserveOpened = useCallback(
      (id: number): boolean => {
        return (
          id === selectedReserveId ||
          (isTableView && !forceClosedReserveIds.includes(id) && openedReserveIds.includes(id))
        );
      },
      [selectedReserveId, isTableView, forceClosedReserveIds, openedReserveIds],
    );

    // Автоматически открывать первый резерв в режиме просмотра стола
    useEffect(() => {
      if (isLoading || !isTableView || reservesView.length === 0) {
        return;
      }

      const isOpenedReserve = reservesView.some((reserve) => isReserveOpened(reserve.id));

      if (!isOpenedReserve && !forceClosedReserveIds.includes(reservesView[0].id)) {
        setOpenedReserveIds([reservesView[0].id]);
      }
    }, [
      isLoading,
      openedReserveIds,
      forceClosedReserveIds,
      isTableView,
      reservesView,
      isReserveOpened,
    ]);

    // Синхронизация с selectedReserveId из URL
    useEffect(() => {
      if (selectedReserveId && !isReserveOpened(selectedReserveId)) {
        setOpenedReserveIds([selectedReserveId]);
        setForceClosedReserveIds((prev) => prev.filter((id) => id !== selectedReserveId));
      }
    }, [selectedReserveId, isReserveOpened]);

    useImperativeHandle(
      ref,
      () => ({
        scrollToReserve: (reserveId: number) => {
          const element = scrollToRefs.current.get(reserveId);
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'nearest',
            });
          }
        },
      }),
      [],
    );

    const handleExpand = (reserveId: number) => {
      if (onReserveExpand) {
        onReserveExpand(reserveId);
      } else {
        toggleOpenedReserveId(reserveId);
      }
    };

    const handleCollapse = (reserveId: number) => {
      if (onReserveCollapse) {
        onReserveCollapse(reserveId);
      } else {
        toggleOpenedReserveId(reserveId);
      }
    };

    const handleClick = (reserveId: number) => {
      if (onReserveClick) {
        onReserveClick(reserveId);
      } else {
        handleExpand(reserveId);
      }
    };

    if (children) {
      return (
        <>
          {children({
            reservesView,
            openedReserveIds,
            forceClosedReserveIds,
            toggleOpenedReserveId,
            isReserveOpened,
          })}
        </>
      );
    }

    if (isLoading) {
      return null;
    }

    if (!reservesView?.length) {
      return renderEmpty ? <>{renderEmpty()}</> : null;
    }

    return (
      <>
        {reservesView.map((scheduleItem, i) => {
          const hr =
            i > 0 ? <HR margin={compact ? 12 : 24} size={compact ? 1 : undefined} /> : null;
          const isOpened = isReserveOpened(scheduleItem.id);

          return (
            <Fragment key={scheduleItem.id}>
              {hr}
              <div
                ref={(el) => {
                  if (el) {
                    scrollToRefs.current.set(scheduleItem.id, el);
                  } else {
                    scrollToRefs.current.delete(scheduleItem.id);
                  }
                }}
              >
                {isOpened ? (
                  <OpenedReserveWidget
                    onCollapse={() => {
                      if (isTableView) {
                        handleCollapse(scheduleItem.id);
                      } else {
                        // На среднем экране (не режим просмотра стола) используем onReserveCollapse если он передан
                        // для сброса URL (как на десктопе)
                        if (onReserveCollapse) {
                          onReserveCollapse(scheduleItem.id);
                        } else {
                          handleCollapse(scheduleItem.id);
                        }
                      }
                    }}
                    onCancel={
                      !scheduleItem.isBanquet && !scheduleItem.openedTableDate
                        ? () => onCancel?.(scheduleItem)
                        : undefined
                    }
                    isCancelDisabled={isCancelDisabled}
                    scheduleReserve={scheduleItem}
                    tablesSchedule={tablesSchedule}
                    restaurantTz={restaurantTz}
                    // compact={compact}
                  />
                ) : (
                  <ReserveWidget
                    onExpand={() => {
                      if (isTableView) {
                        handleExpand(scheduleItem.id);
                      } else {
                        // На среднем экране (не режим просмотра стола) используем onReserveExpand если он передан
                        // для автоматического раскрытия через selectedReserveId (как на десктопе)
                        if (onReserveExpand) {
                          onReserveExpand(scheduleItem.id);
                        } else {
                          handleClick(scheduleItem.id);
                        }
                      }
                    }}
                    scheduleReserve={scheduleItem}
                    tablesSchedule={tablesSchedule}
                    restaurantTz={restaurantTz}
                    // compact={compact}
                  />
                )}
              </div>
            </Fragment>
          );
        })}
      </>
    );
  },
);

ReservesListContainer.displayName = 'ReservesListContainer';
